<script setup lang="ts">
import { computed } from 'vue';
import { useI18nLocalize } from '@/i18n/i18nLocalize';
import _ from 'lodash';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { getTextVariant } from '../../../utils/stringUtils';
import { useClientCopyComposable } from './useClientCopyComposable';
import { useModalComposable } from '@/views/composable';
import InputText from 'primevue/inputtext';

const props = defineProps<{
  rooms: object[];
  data: any;
  isEdit: boolean;
  isPriceValid: boolean;
  isEditPricing: any;
  optionIndex: number;
  tourIndex: number;
  tourInclusions: string;
  isHotel?: boolean;
}>();

const emits = defineEmits<{
  'update:isEditPricing': [value: any];
  'update:isEdit': [value: any];
  'update:isPriceValid': [value: any];
}>();

const { i18nLocale } = useI18nLocalize();
const { t, $t } = i18nLocale(props.data?.lang);
const { renderFrTitleTable, formatCurrency } = useClientCopyComposable(props.data?.lang, props.data?.currency);
const { handleOpenErrorMessage } = useModalComposable();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const headers = [$t('common.single'), $t('common.double'), $t('common.triple'), $t('common.quad'), $t('common.5_in_room'), $t('common.6_in_room')];
const typePrices = ['singlePrice', 'doublePrice', 'triplePrice', 'quadPrice', 'fiveInRoomPrice', 'sixInRoomPrice'];

const isEdit = computed({
  get() {
    return props.isEdit;
  },
  set(value) {
    emits('update:isEdit', value);
  }
});

const isEditPricing = computed({
  get() {
    return props.isEditPricing;
  },
  set(value) {
    emits('update:isEditPricing', value);
  }
});

const isPriceValid = computed({
  get() {
    return props.isPriceValid;
  },
  set(value) {
    emits('update:isPriceValid', value);
  }
});

const changeEditPricing = (optionIndex: number, tourIndex: number, index: number, status: boolean) => {
  isEdit.value = status;
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isEditPricing.value[optionIndex][tourIndex][index] = status;
};

const vCheckPrice = {
  mounted(el: any) {
    el.setAttribute('data-price', el.value.replace(/[^\d.-]/g, ''));
    el.addEventListener('blur', () => {
      const oldValue = el.getAttribute('data-price');
      const newValue = el.value.replace(/[^\d.-]/g, '');
      if (parseFloat(newValue) < parseFloat(oldValue)) {
        el.classList.add('p-invalid');
        isPriceValid.value = false;
      }
    });
  },
  updated(el: any) {
    const oldValue = el.getAttribute('data-price');
    const newValue = el.value.replace(/[^\d.-]/g, '');
    if (parseFloat(newValue) > parseFloat(oldValue)) {
      el.classList.remove('p-invalid');
      isPriceValid.value = true;
    }
  }
};

const checkRunOfHouse = (roomData: any) => {
  if (roomData.code === 'ROH') {
    return `${getTextVariant(props.data?.lang, roomData.longName, roomData.longNameFr)} (${roomData.code})`;
  } else {
    return getTextVariant(props.data?.lang, roomData.longName, roomData.longNameFr);
  }
};
</script>
<template>
  <tr v-if="rooms.length">
    <td colspan="3" class="pt-3 text-xl">
      <span class="flex justify-content-between align-items-center">
        <span>{{ isHotel ? $t('client_copy.room_category_pricing') : $t('client_copy.tour_category_pricing') }} *</span>
        <span class="text-xs">*{{ $t('client_copy.pricing_is_per_person') }}</span>
      </span>
    </td>
  </tr>

  <tr v-if="rooms.length">
    <td colspan="3">
      <table class="table-child w-full">
        <thead>
          <tr>
            <th class="hidden" v-for="time in 10" :key="time"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(room, index) in rooms" :key="index">
            <div v-if="index !== 0" class="mb-5"></div>
            <tr class="background-grey text-sm">
              <td class="text-sm w-2">{{ isHotel ? $t('client_copy.room_category') : $t('management.tour.list_tour_detail.tour_type') }}</td>
              <td v-for="header in headers" :key="header" class="text-sm text-center">{{ header }}</td>
              <td class="text-sm text-center" v-for="(_column, indexColumn) in room.price.occupanciesEdit.childPrices" :key="'column' + indexColumn">
                {{ $t('common.child') }} {{ renderFrTitleTable(room.price.occupanciesEdit.childPrices[indexColumn], $t) }}
              </td>
              <td class="text-sm text-center">
                {{ $t('common.infant') }} <span class="no-wrap">{{ renderFrTitleTable(room.price.occupanciesEdit.infantPrice, $t) }}</span>
              </td>
              <td class="text-sm text-center" v-if="authorizations?.my_quote?.client_copy_edit" colspan="3">{{ $t('common.action') }}</td>
            </tr>
            <tr class="text-sm data_price">
              <td class="text-sm">{{ isHotel ? checkRunOfHouse(room) : tourInclusions }}</td>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.singlePrice) }}</td>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.doublePrice) }}</td>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.triplePrice) }}</td>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.quadPrice) }}</td>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.fiveInRoomPrice) }}</td>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.sixInRoomPrice) }}</td>
              <template v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <td class="text-sm text-center white-space-nowrap" v-for="(_col, colIndex) in room.price.sellingPrice.childPrices" :key="'col' + colIndex">
                  {{ formatCurrency(room.price.sellingPrice.childPrices[colIndex]) }}
                </td>
              </template>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.infantPrice) }}</td>
              <td v-if="!_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-sm text-center white-space-nowrap w-1" colspan="3" rowspan="2">
                <i v-if="authorizations?.my_quote?.client_copy_edit" class="pi pi-pencil cursor-pointer p-1" @click="changeEditPricing(optionIndex, tourIndex, index, true)"></i>
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <InputText v-check-price v-model="room.price.sellingPrice.singlePrice" focus type="number" class="w-full text-center" />
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <InputText v-check-price v-model="room.price.sellingPrice.doublePrice" type="number" class="w-full text-center" />
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <InputText v-check-price v-model="room.price.sellingPrice.triplePrice" type="number" class="w-full text-center" />
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <InputText v-check-price v-model="room.price.sellingPrice.quadPrice" type="number" class="w-full text-center" />
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <InputText v-check-price v-model="room.price.sellingPrice.fiveInRoomPrice" type="number" class="w-full text-center" />
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <InputText v-check-price v-model="room.price.sellingPrice.sixInRoomPrice" type="number" class="w-full text-center" />
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" v-for="(_col, colIndex) in room.price.sellingPrice.childPrices" :key="'col2' + colIndex">
                <InputText v-check-price v-model="room.price.sellingPrice.childPrices[colIndex]" type="number" class="w-full text-center" />
              </td>
              <td v-show="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)">
                <InputText v-check-price v-model="room.price.sellingPrice.infantPrice" type="number" class="w-full text-center" />
              </td>
              <td v-if="_.get(isEditPricing, `[${optionIndex}][${tourIndex}][${index}]`)" class="text-center" colspan="3" rowspan="2">
                <i class="pi pi-check text-cyan-500 cursor-pointer p-1" @click="changeEditPricing(optionIndex, tourIndex, index, false)"></i>
              </td>
            </tr>
            <tr class="text-sm">
              <td class="text-sm">{{ $t('client_copy.taxes_fees_surcharges') }}</td>
              <td v-for="typePrice in typePrices" :key="typePrice" class="text-center text-sm white-space-nowrap">{{ formatCurrency(room.price.taxes[typePrice]) }}</td>
              <td class="text-sm text-center" v-for="(_column2, indexColumn2) in room.price.taxes.childPrices" :key="'column2' + indexColumn2">
                {{ formatCurrency(room.price.taxes.childPrices[indexColumn2]) }}
              </td>
              <td class="text-center text-sm white-space-nowrap">{{ formatCurrency(room.price.taxes.infantPrice) }}</td>
            </tr>
            <template v-if="room.mandatoryInfo">
              <tr class="background-grey text-sm">
                <td colspan="100" class="text-sm">{{ $t('client_copy.mandatory_information') }}</td>
              </tr>
              <tr class="text-sm">
                <td colspan="100" class="text-sm">
                  {{ room.mandatoryInfo }}
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </td>
  </tr>
</template>
